import React, { useState } from "react";
import { Pagination, Table } from "flowbite-react";
import SidbarComponent from "./SidbarComponent";
import TopBarComponent from "./TopBarComponent";
import { FooterComponents } from "./FooterComponent";
import axios from "axios";
import { getToken, getUserRole } from '../utils/auth';
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Search, FileText, Users, Loader2, Filter, Download } from "lucide-react";

export default function BursaryApplication() {
  const [busaryApplications, setBusaryApplications] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchbusaryApplications = async (page = 1) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL +
          "hec/busaryApplication/getAllApplications",
        {
          headers: {
            Authorization: "Bearer " + getToken(),
          },
          params: {
            page: page - 1,
          },
        }
      );
      const busaryApplications = await response.data;
      setBusaryApplications(busaryApplications["content"]);
      setTotalPage(busaryApplications["totalPages"]);
      return busaryApplications;
    } catch (error) {
      toast.error("Failed to fetch bursary applications", {
        position: "top-center",
        autoClose: 5000,
        theme: "colored",
      });
    }
  };

  const onPageChange = (page) => {
    fetchbusaryApplications(page);
    setCurrentPage(page);
  };

  const { isPending, error, data } = useQuery({
    queryKey: ["bursaryApplicationId"],
    queryFn: () => fetchbusaryApplications(),
    retry: 3,
    staleTime: 10000,
  });

  // Filter applications based on search term
  const filteredApplications = busaryApplications.filter(data =>
    data.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    data.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    data.registrationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    data.indexNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex flex-col">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0"
        aria-label="Sidebar"
      >
        <SidbarComponent />
      </aside>

      <div className="p-6 sm:ml-64 transition-all duration-300 flex-1 flex flex-col">
        {/* Page Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-14 h-14 bg-gradient-to-r from-cyan-600 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Bursary Applications</h1>
                  <p className="text-gray-600 mt-1">Manage and review student bursary applications</p>
                </div>
              </div>
              <div className="mt-6 sm:mt-0 flex items-center space-x-4">
                <div className="flex items-center space-x-3 text-sm text-gray-600 bg-gradient-to-r from-cyan-50 to-blue-50 px-4 py-3 rounded-xl border border-cyan-200">
                  <Users className="w-5 h-5 text-cyan-600" />
                  <span className="font-semibold">{busaryApplications.length} Applications</span>
                </div>
                <button className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02]">
                  <Download className="w-5 h-5" />
                  <span>Export</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Search Header */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-b border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="relative max-w-md flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200 shadow-sm placeholder-gray-500"
                  placeholder="Search applications by name, registration or index number..."
                />
              </div>
              <div className="flex items-center space-x-3">
                <button className="flex items-center space-x-2 px-5 py-3 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200 shadow-sm">
                  <Filter className="w-4 h-4" />
                  <span>Filter</span>
                </button>
              </div>
            </div>
          </div>

          {/* Table Content */}
          <div className="p-6">
            {isPending && (getUserRole() === "Admin" || getUserRole() === "DG") ? (
              <div className="flex items-center justify-center py-24">
                <div className="text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-r from-cyan-200 to-blue-200 rounded-full animate-pulse mx-auto mb-4"></div>
                    <Loader2 className="w-8 h-8 text-cyan-600 animate-spin absolute top-4 left-1/2 transform -translate-x-1/2" />
                  </div>
                  <p className="text-gray-700 text-xl font-semibold mb-2">Loading Applications</p>
                  <p className="text-gray-500">Please wait while we fetch the latest data...</p>
                </div>
              </div>
            ) : getUserRole() === "Admin" || getUserRole() === "DG" ? (
              <div>
                {filteredApplications.length > 0 ? (
                  <>
                    {/* Enhanced Table */}
                    <div className="overflow-hidden border border-gray-200 rounded-xl shadow-sm">
                      <Table striped className="w-full">
                        <Table.Head className="bg-gradient-to-r from-gray-50 to-cyan-50">
                          <Table.HeadCell className="text-gray-700 font-semibold">Registration #</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Student Name</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Gender</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Index Number</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">S6 Aggregate</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Qualification</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Academic Year</Table.HeadCell>
                          <Table.HeadCell className="text-gray-700 font-semibold">Study Area</Table.HeadCell>
                        </Table.Head>
                        <Table.Body className="divide-y divide-gray-200">
                          {filteredApplications.map((data, index) => (
                            <Table.Row
                              className="bg-white hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 transition-all duration-200 border-b border-gray-100"
                              key={data.registrationNumber}
                            >
                              <Table.Cell className="whitespace-nowrap">
                                <div className="font-semibold text-gray-900 text-sm">
                                  {data.registrationNumber}
                                </div>
                              </Table.Cell>
                              <Table.Cell>
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md">
                                    {data.firstName?.charAt(0)}{data.lastName?.charAt(0)}
                                  </div>
                                  <div>
                                    <div className="font-semibold text-gray-900 text-sm">
                                      {data.firstName} {data.lastName}
                                    </div>
                                  </div>
                                </div>
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                  data.gender === 'Male' 
                                    ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                                    : 'bg-pink-100 text-pink-800 border border-pink-200'
                                }`}>
                                  {data.gender}
                                </span>
                              </Table.Cell>
                              <Table.Cell className="text-gray-600 text-sm font-medium">
                                {data.indexNumber}
                              </Table.Cell>
                              <Table.Cell>
                                <span className={`inline-flex px-3 py-1 text-xs font-bold rounded-full ${
                                  parseInt(data.s6Aggregate) <= 15
                                    ? 'bg-green-100 text-green-800 border border-green-200'
                                    : parseInt(data.s6Aggregate) <= 20
                                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                    : 'bg-red-100 text-red-800 border border-red-200'
                                }`}>
                                  {data.s6Aggregate}
                                </span>
                              </Table.Cell>
                              <Table.Cell>
                                <div className="text-sm text-gray-900 max-w-xs truncate font-medium">
                                  {data["qualification"]?.qualificationName}
                                </div>
                              </Table.Cell>
                              <Table.Cell className="text-gray-600 text-sm font-medium">
                                {data.completionYearCode}
                              </Table.Cell>
                              <Table.Cell>
                                <div className="text-sm text-gray-900 max-w-xs truncate font-medium">
                                  {data["qualification"]?.["studyAreaId"]?.studyAreaName}
                                </div>
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </div>

                    {/* Enhanced Pagination */}
                    {totalPage > 1 && (
                      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                        <div className="text-sm text-gray-600 font-medium">
                          Showing page {currentPage} of {totalPage}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPage}
                            onPageChange={onPageChange}
                            showIcons
                            theme={{
                              pages: {
                                base: "xs:mt-0 mt-2 inline-flex items-center -space-x-px",
                                showIcon: "inline-flex",
                                previous: {
                                  base: "ml-0 rounded-l-xl border border-gray-300 bg-white py-3 px-4 text-gray-500 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 hover:text-cyan-700 transition-all duration-200 shadow-sm",
                                },
                                next: {
                                  base: "rounded-r-xl border border-gray-300 bg-white py-3 px-4 text-gray-500 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 hover:text-cyan-700 transition-all duration-200 shadow-sm",
                                },
                                selector: {
                                  base: "w-12 border border-gray-300 bg-white py-3 text-gray-500 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-blue-50 hover:text-cyan-700 transition-all duration-200 shadow-sm",
                                  active: "bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-600 hover:to-blue-600 border-cyan-500 shadow-md",
                                },
                              },
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-24">
                    <div className="w-20 h-20 bg-gradient-to-r from-gray-200 to-cyan-200 rounded-full flex items-center justify-center mx-auto mb-6">
                      <FileText className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-gray-700 text-xl font-semibold mb-2">No Applications Found</p>
                    <p className="text-gray-500 max-w-md mx-auto">
                      {searchTerm 
                        ? 'No applications match your search criteria. Try adjusting your search terms.' 
                        : 'No bursary applications have been submitted yet.'
                      }
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-24">
                <div className="w-20 h-20 bg-gradient-to-r from-red-200 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 text-red-400" />
                </div>
                <p className="text-red-600 text-xl font-semibold mb-2">Access Denied</p>
                <p className="text-gray-600">You don't have permission to view this page</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <FooterComponents />
      </div>
    </div>
  );
}
